import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup
} from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc, 
  getDoc, 
  setDoc 
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../lib/firebase';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  name: string | null;
  phone: string | null;
  role: 'admin' | 'pizzeria' | 'user';
  slices: number;
  trialStartDate: string | null;
  trialDays: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ user: User, isNewUser: boolean, hasPizzerie: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Carica i dati utente da Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.data();
          
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            name: userData?.name || firebaseUser.displayName,
            phone: userData?.phone || null,
            role: userData?.role || 'user',
            slices: userData?.slices || 0,
            trialStartDate: userData?.trialStartDate,
            trialDays: userData?.trialDays
          });
        } catch (err) {
          console.error('Error loading user data:', err);
          setError('Errore nel caricamento dei dati utente');
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError('Errore durante il login: ' + err.message);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      // Verifica se l'utente è il super admin
      const isGiosiAdmin = firebaseUser.email === 'giosi69@gmail.com';
      
      // Verifica se l'utente esiste già
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      const userData = userDoc.data();
      const isNewUser = !userData;

      // Verifica se l'utente ha già pizzerie
      const pizzerieRef = collection(db, 'pizzerie');
      const pizzerieQuery = query(pizzerieRef, where('userId', '==', firebaseUser.uid));
      const pizzerieSnapshot = await getDocs(pizzerieQuery);
      const hasPizzerie = !pizzerieSnapshot.empty;
      
      // Crea/aggiorna il documento utente
      const newUserData = {
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        displayName: firebaseUser.displayName,
        phone: '',
        role: isGiosiAdmin ? 'admin' : 'pizzeria',
        slices: isNewUser && !isGiosiAdmin ? 1 : 0,
        updatedAt: new Date().toISOString(),
        trialStartDate: isNewUser ? new Date().toISOString() : null,
        trialDays: isNewUser ? 30 : 0
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), newUserData, { merge: true });
      
      const user: User = {
        uid: firebaseUser.uid,
        email: newUserData.email,
        name: newUserData.name,
        displayName: newUserData.displayName,
        phone: newUserData.phone,
        role: newUserData.role as 'admin' | 'pizzeria' | 'user',
        slices: newUserData.slices,
        trialStartDate: newUserData.trialStartDate,
        trialDays: newUserData.trialDays
      };

      return { user, isNewUser, hasPizzerie };
    } catch (err: any) {
      console.error('Google Sign In Error:', err);
      setError('Errore durante il login con Google: ' + err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err: any) {
      setError('Errore durante il logout: ' + err.message);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};